import {
  collection,
  doc,
  getCountFromServer,
  getAggregateFromServer,
  getDoc,
  query,
  where,
  sum,
} from 'firebase/firestore';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import Subscription from '../../models/Subscription';
import Failure from '../../../core/Failure';
import { round2digits } from '../../../core/utils/commonUtils';
import { NotificationType } from "@leedpulse-monorepo/enums";
import { Dayjs } from 'dayjs';
import countScheduledMessages from '../jobs/countScheduledMessages';

type GetSubscriptionByIdResponse = {
  subscription: Subscription;
  contactCount: number;
  smsCount: number;
  jobsCount: number;
  totalCostSms: number;
  notificationsPendingCount: number;
  notificationsCount: number;
  replyRate: number;
  engagedCount: number;
  qualificationDistribution: {
    interested: number;
    considering: number;
    not_interested: number;
    not_responsive: number;
    not_available: number;
  };
};

async function getSubscriptionById(
  subscriptionId: string,
  scenarioId?: string,
  userUid?: string,
  period?: [Dayjs | null, Dayjs | null]
): Promise<GetSubscriptionByIdResponse | null> {
  try {
    const db = firestore;

    const subscriptionDocRef = await getDoc(
      doc(db, FirestoreCollections.subscriptions, subscriptionId)
    );

    if (!subscriptionDocRef.exists()) {
      return null;
    }

    const subscription = Subscription.fromFirestore(subscriptionDocRef);

    const contactsCollection = collection(db, FirestoreCollections.contacts);
    const smsCollection = collection(db, FirestoreCollections.sms);
    const notificationsCollection = collection(db, FirestoreCollections.notifications);

    const contactConstraints = [];

    if (scenarioId) {
      contactConstraints.push(where('contact.prospect_category', '==', scenarioId));
    }

    if (userUid) {
      contactConstraints.push(where('contact.owner', '==', userUid));
    }

    if (period && period.length === 2 && period[0] && period[1]) {
      contactConstraints.push(where('qualification_ai.timestamp', '>=', period[0].valueOf()));
      contactConstraints.push(where('qualification_ai.timestamp', '<=', period[1].valueOf()));
    }

    const [
      subscriptionContactsSnapshot,
      subscriptionContactsEngagedSnapshot,
      repliedContactsSnapshot,
      subscriptionSmsSnapshot,
      subscriptionScheduledMessagesCount,
      notificationsPendingSnapshot,
      notificationsSnapshot,
      interestedContactsSnapshot,
      consideringContactsSnapshot,
      notInterestedContactsSnapshot,
      notResponsiveContactsSnapshot,
      notAvailableContactsSnapshot
    ] = await Promise.all([
      getCountFromServer(query(contactsCollection, where('id_api_key', '==', subscription.apiKey))),
      getCountFromServer(query(contactsCollection, where('id_api_key', '==', subscription.apiKey), where("last_activity.role", "in", ["user", "assistant"]))),
      getCountFromServer(query(contactsCollection, where('id_api_key', '==', subscription.apiKey), where('has_replied', '==', true))),
      getCountFromServer(query(smsCollection, where('id_api_key', '==', subscription.apiKey), where('role', '!=', 'system'))),
      countScheduledMessages([where('id_api_key', '==', subscription.apiKey)]),
      getCountFromServer(
        query(notificationsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('type', '==', NotificationType.TODO)
        )
      ),
      getCountFromServer(
        query(notificationsCollection,
          where('id_api_key', '==', subscription.apiKey)
        )
      ),
      getCountFromServer(
        query(
          contactsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('qualification_ai.qualification', '==', 'interested'),
          ...contactConstraints
        )
      ),
      getCountFromServer(
        query(
          contactsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('qualification_ai.qualification', '==', 'considering'),
          ...contactConstraints
        )
      ),
      getCountFromServer(
        query(
          contactsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('qualification_ai.qualification', '==', 'not_interested'),
          ...contactConstraints
        )
      ),
      getCountFromServer(
        query(
          contactsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('qualification_ai.qualification', '==', 'not_responsive'),
          ...contactConstraints
        )
      ),
      getCountFromServer(
        query(
          contactsCollection,
          where('id_api_key', '==', subscription.apiKey),
          where('qualification_ai.qualification', '==', 'not_available'),
          ...contactConstraints
        )
      )
    ])

    const contactCount = subscriptionContactsSnapshot.data().count ?? 0;

    // Count contacts engaged
    const contactEngagedCount = subscriptionContactsEngagedSnapshot.data().count ?? 0;

    // Count replied contacts
    const repliedContactsCount = repliedContactsSnapshot.data().count ?? 0;

    // Calculate reply rate
    const replyRate = contactCount > 0 ? round2digits((repliedContactsCount / contactEngagedCount) * 100) : 0;

    // SMS Cost sum
    const queryTotalCostSms = query(smsCollection, where('id_api_key', '==', subscription.apiKey));
    const totalCostSmsSnapshot = await getAggregateFromServer(queryTotalCostSms, {
      totalCostSms: sum('price'),
    });

    // Contacts Interested
    const interestedCount = interestedContactsSnapshot.data().count ?? 0;

    // Contacts Considering
    const consideringCount = consideringContactsSnapshot.data().count ?? 0;

    // Contacts Not Interested
    const notInterestedCount = notInterestedContactsSnapshot.data().count ?? 0;

    // Contacts Not Responsive
    const notResponsiveCount = notResponsiveContactsSnapshot.data().count ?? 0;

    // Contacts Not Available
    const notAvailableCount = notAvailableContactsSnapshot.data().count ?? 0;

    return {
      subscription,
      contactCount: subscriptionContactsSnapshot.data().count ?? 0,
      smsCount: subscriptionSmsSnapshot.data().count ?? 0,
      jobsCount: subscriptionScheduledMessagesCount,
      totalCostSms: round2digits(totalCostSmsSnapshot.data().totalCostSms) ?? 0,
      notificationsPendingCount: notificationsPendingSnapshot.data().count ?? 0,
      notificationsCount: notificationsSnapshot.data().count ?? 0,
      replyRate,
      engagedCount: contactEngagedCount ?? 0,
      qualificationDistribution: {
        interested: interestedCount,
        considering: consideringCount,
        not_interested: notInterestedCount,
        not_responsive: notResponsiveCount,
        not_available: notAvailableCount
      },
    };
  } catch (e) {
    console.log('error: ', e);
    throw new GetSubscriptionByIdFailure();
  }
}

export class GetSubscriptionByIdFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getSubscriptionById;
